import { Layout } from "@/components/layout";
import {
  Banknote,
  Upload,
  Mail,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  HelpCircle,
  Send,
  Filter,
  Eye,
} from "lucide-react";

function Section({
  icon: Icon,
  title,
  children,
}: {
  icon: any;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="bg-white rounded-2xl border border-slate-200 p-6">
      <h2 className="text-lg font-semibold text-duffells-grey flex items-center gap-2 mb-4">
        <Icon className="w-5 h-5 text-primary" />
        {title}
      </h2>
      <div className="text-sm text-duffells-grey/80 space-y-3 leading-relaxed">
        {children}
      </div>
    </section>
  );
}

function Pill({
  tone,
  children,
}: {
  tone: "slate" | "emerald" | "sky" | "amber" | "indigo" | "red";
  children: React.ReactNode;
}) {
  const map = {
    slate: "bg-slate-100 text-slate-700",
    emerald: "bg-emerald-100 text-emerald-700",
    sky: "bg-sky-100 text-sky-700",
    amber: "bg-amber-100 text-amber-700",
    indigo: "bg-indigo-100 text-indigo-700",
    red: "bg-red-100 text-red-700",
  } as const;
  return (
    <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium capitalize ${map[tone]}`}>
      {children}
    </span>
  );
}

export default function CashReceiptsGuidePage() {
  return (
    <Layout>
      <div className="space-y-6 max-w-4xl">
        <div>
          <h1 className="text-2xl font-bold text-duffells-grey flex items-center gap-2">
            <HelpCircle className="w-6 h-6 text-primary" />
            BACS Reconciliation Guide
          </h1>
          <p className="text-sm text-duffells-grey/60 mt-1">
            How the BACS page turns raw bank-statement lines into invoice payments posted to Merlin.
          </p>
        </div>

        <Section icon={Banknote} title="What this page is for">
          <p>
            The BACS tab on <strong>Cash Receipts</strong> reconciles incoming bank payments
            (the Barclays Sales Finance Daily Client Summary) against customer remittance
            advices, and posts the matched invoices to Merlin as CashReceipts. Our goal on
            this page is the same every morning: <strong>get every line on today's statement
            matched</strong>, then post to Merlin.
          </p>
        </Section>

        <Section icon={Upload} title="Step 1 — Upload the daily statement">
          <p>
            Drop the Daily Client Summary CSV from Barclays into the upload box. Each file is
            parsed once: if the same filename has already been processed it is rejected, so
            you can't double-count a day.
          </p>
          <p>
            Each row in the CSV becomes a <strong>bank payment</strong>. The parser extracts
            hints from the narrative where it can:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Account hint</strong> — a Merlin customer account code in the narrative.</li>
            <li><strong>Invoice hint</strong> — a quoted invoice reference.</li>
            <li><strong>EFT reference</strong> — the raw payer-supplied reference.</li>
          </ul>
        </Section>

        <Section icon={ArrowRight} title="Step 2 — Automatic reconciliation">
          <p>
            For every new bank payment the reconciler tries, in order:
          </p>
          <ol className="list-decimal pl-6 space-y-2">
            <li>
              <strong>Match an existing remittance.</strong> If a customer has already emailed
              us a remittance for the same amount AND the bank narrative agrees with either
              the remittance's resolved account or one of its invoice references, the two are
              linked and the payment becomes <Pill tone="emerald">matched</Pill>.
            </li>
            <li>
              <strong>Auto-allocate against unpaid invoices.</strong> If we can resolve the
              customer (from the account hint, or from a unique invoice hint), we pull their
              open invoices from Merlin and look for any subset whose balances sum
              <em> exactly</em> to the payment amount. Auto-allocation only fires when there
              is exactly <strong>one</strong> such subset — otherwise it's genuinely ambiguous
              and we fall through. On success the payment becomes
              {" "}<Pill tone="emerald">auto allocated</Pill>.
            </li>
            <li>
              <strong>Email the customer for a remittance.</strong> If we can resolve the
              customer but can't find a unique invoice combination, we email their accounts
              contact asking for a remittance advice. The payment moves to
              {" "}<Pill tone="sky">awaiting remittance</Pill>. When the customer replies, the
              incoming-remittance flow picks it up and links it back automatically. If no
              reply arrives within <strong>48 hours</strong>, posting the whole payment
              unallocated (on-account) becomes available — see "Posting to Merlin" below.
            </li>
          </ol>
          <p>
            If none of those work — usually because we couldn't identify the customer at all,
            or the customer has no email on file — the payment lands in
            {" "}<Pill tone="amber">needs review</Pill> with a note explaining why.
          </p>
        </Section>

        <Section icon={CheckCircle2} title="Eligibility rules for auto-allocation">
          <p>
            Auto-allocation is intentionally strict — we'd rather punt to "ask for a
            remittance" than mis-post a payment. An invoice in Merlin is only eligible if:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>It has a positive outstanding balance.</li>
            <li>No prior payments have been recorded against it.</li>
            <li>It has exactly <strong>one</strong> canonical unpaid sltran row (invoice_total &gt; 0, payment_amount = 0).</li>
            <li>
              The aggregate balance equals that canonical row's invoice_total — i.e. there are
              no credit-note or adjustment rows shifting the figures.
            </li>
          </ul>
          <p>
            And the subset rule: there must be <strong>exactly one</strong> combination of
            eligible invoices that sums to the bank-payment amount. Two valid combinations →
            ambiguous → we ask the customer.
          </p>
        </Section>

        <Section icon={AlertCircle} title="Status reference">
          <div className="space-y-2">
            <div><Pill tone="slate">unmatched</Pill> — newly imported, not yet processed.</div>
            <div><Pill tone="slate">processing</Pill> — the reconciler is mid-run on this row (transient).</div>
            <div><Pill tone="emerald">matched</Pill> — linked to a customer remittance advice.</div>
            <div><Pill tone="emerald">auto allocated</Pill> — uniquely matched to a subset of unpaid invoices.</div>
            <div><Pill tone="sky">awaiting remittance</Pill> — customer has been emailed; waiting for their reply.</div>
            <div><Pill tone="amber">needs review</Pill> — manual intervention required (see notes column).</div>
            <div><Pill tone="slate">ignored</Pill> — manually marked as not relevant to reconciliation.</div>
          </div>
        </Section>

        <Section icon={Send} title="Posting to Merlin">
          <p>
            Once a payment is <Pill tone="emerald">matched</Pill> or
            {" "}<Pill tone="emerald">auto allocated</Pill> and has a resolved customer
            account, the <strong>Mark as Paid in Merlin</strong> button posts a CashReceipt
            per invoice via the Merlin SOAP API (routed through the QuotaGuard proxy).
          </p>
          <p>
            We pre-flight every invoice in one batched call first — if any single invoice is
            ambiguous, already paid, or doesn't match the expected amount, we refuse to post
            anything. Only once all checks pass do we start posting one CashReceipt per
            invoice in sequence.
          </p>
          <p>
            <strong>Posting on-account (unallocated).</strong> When a payment can't be matched
            or allocated, the whole amount can be posted on-account (transaction id -1). To
            protect against booking cash blind, the <strong>Post on-account</strong> button is
            gated: a remittance request must have been emailed to the customer, and at least
            {" "}<strong>48 hours</strong> must have passed with <strong>no reply</strong>. Until
            then the button is disabled with an explanation. If the customer replies, the gate
            clears immediately and you should act on their remittance instead. An operator can
            still <strong>override and post unallocated early</strong> via the inline link — the
            override is recorded in the audit log. This gate applies only to the whole-payment
            on-account post, not to the per-line "Pay unallocated" action on a matched remittance.
          </p>
          <p>
            <strong>Idempotency:</strong> the moment posting starts we atomically claim the
            row. A second click (or concurrent caller) sees it as claimed and bails out.
            After every successful per-invoice post we incrementally persist a forensic
            record, so even if the run crashes we know exactly which invoices already hit
            Merlin.
          </p>
          <p>
            If a run starts but doesn't finish — process crash, network blip — the row shows
            an amber <Pill tone="amber">Merlin: investigate</Pill> badge listing what was
            recorded. Retry is intentionally blocked: an operator must investigate before
            anything else is posted.
          </p>
          <p>After a successful run you'll see one of:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><Pill tone="indigo">Posted to Merlin</Pill> — every invoice accepted with code 0.</li>
            <li><Pill tone="red">Merlin: partial</Pill> — at least one invoice came back with an error code (hover for details).</li>
          </ul>
        </Section>

        <Section icon={Filter} title="Filtering and what's hidden">
          <p>
            The status dropdown at the top of the bank-payments table defaults to
            {" "}<strong>Hide matched</strong> so the list focuses on what still needs work.
            Use <strong>All</strong> to see everything, or pick a specific status.
          </p>
          <p>
            Two categories are <strong>always hidden</strong> on the BACS page, because they
            don't belong in this reconciliation flow:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Stripe</strong> settlements (narrative contains "Stripe") — these reconcile against the Stripe payout flow, not customer invoices.</li>
            <li><strong>BCARD</strong> rows (narrative starts with "BCARD") — Barclaycard merchant settlements, reconciled elsewhere.</li>
          </ul>
          <p>
            The count line shows how many of each are hidden so it's never invisible. They
            still exist in the database; this is purely a view filter on the BACS page.
          </p>
        </Section>

        <Section icon={Mail} title="Remittance emails">
          <p>
            When we can't auto-allocate, we email the customer's accounts contact (resolved
            from their Merlin record) with the payment date, amount and bank narrative,
            asking for a list of invoice numbers and amounts. Their reply is picked up by the
            inbound remittance poller and matched back to the awaiting payment automatically.
          </p>
          <p>
            We record the email recipient and timestamp <em>before</em> sending, so even on
            an SMTP failure we never send the same customer two duplicate requests for the
            same payment.
          </p>
        </Section>

        <Section icon={Eye} title="When something's wrong">
          <p>Common cases that land in <Pill tone="amber">needs review</Pill>:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>No customer hints</strong> — narrative was anonymous (e.g. "FASTER PAYMENT"). Match manually from the Remittances tab if a remittance arrives.</li>
            <li><strong>Customer resolved but no email on file</strong> — add an accounts contact in Merlin and re-run reconciliation.</li>
            <li><strong>Ambiguous invoice combinations</strong> — two or more subsets sum to the payment. Wait for the remittance, or allocate manually once you know.</li>
            <li><strong>Merlin returned a SOAP error mid-post</strong> — see the "Merlin: partial" badge tooltip for the failing invoice + error code.</li>
          </ul>
        </Section>
      </div>
    </Layout>
  );
}
